import { useQuery } from '@tanstack/react-query';
import {
  analyticsRpc,
  transformStats,
  transformTopProgram,
  type SignupTrend,
} from '../api';

export function useAdminStats() {
  const query = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: async () => {
      const { data, error } = await analyticsRpc.getStats({});

      if (error) throw error;
      if (!data) throw new Error('No data returned');

      return transformStats(data);
    },
    refetchInterval: 60000, // Refresh every minute
  });

  return {
    stats: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useSignupTrends(days: number = 30) {
  const query = useQuery({
    queryKey: ['admin', 'signup-trends', days],
    queryFn: async () => {
      const { data, error } = await analyticsRpc.getSignupTrends({
        p_days: days,
      });

      if (error) throw error;
      if (!data) throw new Error('No data returned');

      return data as SignupTrend[];
    },
  });

  return {
    trends: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useTopPrograms(
  sortBy: 'downloads' | 'rating' | 'recent' = 'downloads',
  limit: number = 10
) {
  const query = useQuery({
    queryKey: ['admin', 'top-programs', sortBy, limit],
    queryFn: async () => {
      const { data, error } = await analyticsRpc.getTopPrograms({
        p_limit: limit,
        p_sort_by: sortBy,
      });

      if (error) throw error;
      if (!data) throw new Error('No data returned');

      return data.map(transformTopProgram);
    },
  });

  return {
    programs: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
